import React, { FunctionComponent, useEffect, useState } from "react";
import styles from "./AppVersion.module.css";
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Menu,
  MenuItem,
  ListItemText,
} from "@mui/material";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  AppVersionObjectType,
  AppVersionList,
  CustomTextField,
  InputPropsForAddAffiliates,
} from "../../helper";
import { useForm } from "react-hook-form";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_CURRENT_APP_VERSION, UPDATE_VERSION_NOTIFICATION } from "./api/TnCs&PPQuery";
import ToasterWithMessage from "../../components/shared-components/toaster";

const InputField = CustomTextField();

const defaultValues = {
  appOsName: "",
  appVersion: "",
  forceUpdate: true,
};

interface FormdataProps {
  appOsName: string;
  appVersion: string;
  forceUpdate: boolean;
}

const AppVersion: FunctionComponent = () => {
  const [disableBtn, setDisableBtn] = useState<boolean>(true);
  const [anchorElApp, setAnchorElApp] = useState<null | HTMLElement>(null);
  const [appVerisonList, setAppVersionList] = useState<AppVersionList | AppVersionObjectType[]>([]);
  const [appversionOsName, setAppVersionOsName] = useState<string>("");
  const [valueradio, setValueRadio] = useState<string>("");
  const [appversionInput, setAppVersionInput] = useState<string>("");
  const [initialValueForappVersion, setinitialValueForappversion] =
    useState<string>("");
  const [initialValueForceupdate, setinitialValueForceupdate] =
    useState<boolean>();
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    if (typeof window === "undefined") {
      return false;
    }

    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) {
      return savedTheme === "dark";
    }

    return document.documentElement.getAttribute("data-theme") === "dark";
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
    setError,
    clearErrors
  } = useForm({
    defaultValues: defaultValues,
    mode: "onChange",
  });

  const watchFields = watch(["appOsName", "appVersion", "forceUpdate"]);
  const { error, success } = ToasterWithMessage();

  const open = Boolean(anchorElApp);

  const [getCurrentAppVersion, { loading }] = useLazyQuery(
    GET_CURRENT_APP_VERSION,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data) {
          // Check if data.app_version_info is an array or object
          const versionData = data.app_version_info;
          if (Array.isArray(versionData)) {
            setAppVersionList(versionData);
          } else if (versionData && typeof versionData === 'object') {
            // If it's an object, convert to array or handle accordingly
            setAppVersionList([versionData]);
          } else {
            setAppVersionList([]);
          }
        }
      },
    }
  );

  const [updateVersion, { loading: updateLoading }] = useMutation(
    UPDATE_VERSION_NOTIFICATION,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data?.updateAppVersion?.message) {
          success("Version Updated Successfully");
          setinitialValueForappversion(appversionInput);
          setinitialValueForceupdate(valueradio === "Yes" ? true : false);
          setDisableBtn(true);
        }
      },
      onError: () => {
        error("Something went Wrong!");
      },
    }
  );

  const handleClickOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElApp(event.currentTarget);
    getCurrentAppVersion();
  };

  const handleCloseMenu = () => {
    setAnchorElApp(null);
  };

  const handleSelectappversion = (appVersion: AppVersionObjectType) => {
    setAnchorElApp(null);
    setAppVersionOsName(appVersion.app_os_name);
    setValueRadio(appVersion.app_should_force_update ? "Yes" : "No");
    setAppVersionInput(appVersion.app_version);
    setValue("appOsName", appVersion.app_os_name);
    setValue("appVersion", appVersion.app_version);
    setValue("forceUpdate", appVersion.app_should_force_update);
    setinitialValueForappversion(appVersion.app_version);
    setinitialValueForceupdate(appVersion.app_should_force_update);
  };

  const handleChangeAppVersion = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearErrors('appVersion');
    const input = e.target.value;
    const regexPattern = /^(?!0)(?!.*\.\.)[0-9.]*$/;
    if (regexPattern.test(input) && input.length <= 10) {
      setAppVersionInput(e.target.value);
      setValue("appVersion", e.target.value);
    } else {
      setAppVersionInput(appversionInput);
      setValue("appVersion", appversionInput);
    }
  };

  const handleChangeForceUpdate = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setValueRadio(event.target.value);
    setValue("forceUpdate", event.target.value === "Yes" ? true : false);
  };

  const onSubmitAppversion = (data: FormdataProps) => {
    const regexPattern = /^(?!\.)(?!.*\.$)[0-9.]*$/;
    if (data.appOsName.trim() !== "" && data.appVersion.trim() !== "") {
      if(regexPattern.test(data?.appVersion.trim())){
      updateVersion({
        variables: {
          appOsName: data.appOsName,
          appVersion: data.appVersion,
          shouldForceUpdate: data.forceUpdate,
        },
      });
    }else{
       setError('appVersion',{
        type: 'manual',
        message: "Enter a valid App Version",
      })
    }
    }
  };

  const handleReset = () => {
    setAppVersionOsName("");
    setAppVersionInput("");
    setValueRadio("");
    setValue("appOsName", "");
    setValue("appVersion", "");
    setValue("forceUpdate", defaultValues.forceUpdate);
    clearErrors("appVersion");
    setDisableBtn(true);
  };

  useEffect(() => {
    if (appversionOsName !== "") {
      if (
        watch("appVersion") === initialValueForappVersion &&
        watch("forceUpdate") === initialValueForceupdate
      ) {
        setDisableBtn(true);
      } else if (
        watch("appOsName").trim() === "" ||
        watch("appVersion").trim() === "" ||
        valueradio.trim() === ""
      ) {
        setDisableBtn(true);
      } else {
        setDisableBtn(false);
      }
    } else {
      if (
        watch("appOsName").trim() === "" ||
        watch("appVersion").trim() === "" ||
        valueradio.trim() === ""
      ) {
        setDisableBtn(true);
      } else {
        setDisableBtn(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchFields]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", isDarkMode ? "dark" : "light");
    localStorage.setItem("theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  const handleRefresh = () => {
    getCurrentAppVersion();
  };

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  // Helper function to check if appVerisonList is an array
  const getVersionList = (): AppVersionObjectType[] => {
    if (Array.isArray(appVerisonList)) {
      return appVerisonList;
    }
    return [];
  };

  return (
    <>
      {updateLoading ? (
        <div className={styles.circularProgressappVersion}>
          <CircularProgress color="primary" size={35} />
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmitAppversion)}>
          <div className={styles.maindiv}>
            <div className={styles.headerContainer}>
              <div className={styles.titleContainer}>
                <h2 className={styles.pageTitle}>App Version Management</h2>
                <p className={styles.pageSubtitle}>
                  Update the app version details and control whether users are forced to update.
                </p>
              </div>
              <div className={styles.iconContainer}>
                <button
                  type="button"
                  className={styles.iconWrapper}
                  onClick={handleRefresh}
                  title="Refresh app versions"
                  aria-label="Refresh app versions"
                >
                  <RefreshRoundedIcon className={styles.headerIcon} />
                </button>
                <button
                  type="button"
                  className={styles.iconWrapper}
                  onClick={toggleTheme}
                  title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                  aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                >
                  {isDarkMode ? (
                    <LightModeOutlinedIcon className={styles.headerIcon} />
                  ) : (
                    <DarkModeOutlinedIcon className={styles.headerIcon} />
                  )}
                </button>
              </div>
            </div>

            <div className={styles.seconddiv}>
              {/* App Platform Field */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>App Platform</label>
                <div
                  className={styles.selectField}
                  onClick={handleClickOpenMenu}
                >
                  <div className={styles.selectContainer}>
                    <span
                      className={
                        appversionOsName === ""
                          ? styles.placeholderText
                          : styles.selectedText
                      }
                    >
                      {appversionOsName || "--Select--"}
                    </span>
                    <KeyboardArrowDownIcon className={styles.dropdownArrow} />
                  </div>
                </div>
              </div>

              {/* App Version Field */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>App Version</label>
                <InputField
                  variant="outlined"
                  data-testid="appVersion"
                  fullWidth
                  {...register("appVersion", {
                    required: "App version is required",
                  })}
                  value={appversionInput}
                  placeholder="Enter App Version"
                  InputProps={{
                    style: InputPropsForAddAffiliates,
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      minHeight: "52px",
                      backgroundColor: isDarkMode ? "#1a2332" : "#ffffff",
                      border : isDarkMode ? "1px solid #2d3748" : "1px solid #e5e7eb",
                      color: isDarkMode ? "#f1f5f9" : "#111827",
                      "& fieldset": {
                        borderColor: isDarkMode ? "#2d3748" : "#d1d5db",
                        borderWidth: "1.5px",
                      },
                      "&:hover fieldset": {
                        borderColor: "#5D75F8",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#5D75F8",
                        borderWidth: "2px",
                        boxShadow: "0 0 0 4px rgba(93, 117, 248, 0.12)",
                      },
                      "& input": {
                        color: isDarkMode ? "#f1f5f9" : "#111827",
                        fontSize: "14px",
                        padding: "14px 16px",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: isDarkMode ? "#64748b" : "#9ca3af",
                        opacity: 1,
                      },
                    },
                  }}
                  error={Boolean(errors?.appVersion?.message)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeAppVersion(e)
                  }
                />
                {errors?.appVersion?.message && (
                  <span className={styles.errorMsgStyle}>
                    {errors.appVersion.message}
                  </span>
                )}
              </div>

              {/* Force Update Field */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Force Update</label>
                <RadioGroup
                  aria-labelledby="force-update-radio-group"
                  name="force-update"
                  value={valueradio}
                  onChange={handleChangeForceUpdate}
                  className={styles.radioGroup}
                >
                  <div className={styles.radioOption}>
                    <FormControlLabel
                      value="Yes"
                      control={
                        <Radio
                          sx={{
                            color: "#94a3b8",
                            "&.Mui-checked": {
                              color: "#5D75F8",
                            },
                          }}
                        />
                      }
                      label="Yes"
                      classes={{ label: styles.radioLabel }}
                    />
                  </div>
                  <div className={styles.radioOption}>
                    <FormControlLabel
                      value="No"
                      control={
                        <Radio
                          sx={{
                            color: "#94a3b8",
                            "&.Mui-checked": {
                              color: "#5D75F8",
                            },
                          }}
                        />
                      }
                      label="No"
                      classes={{ label: styles.radioLabel }}
                    />
                  </div>
                </RadioGroup>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.ctaContainer}>
              <button
                type="button"
                className={styles.resetButton}
                onClick={handleReset}
              >
                Reset
              </button>
              <button
                type="submit"
                className={`${styles.updateButton} ${disableBtn ? styles.updateButtonDisabled : ''}`}
                disabled={disableBtn}
              >
                Update
              </button>
            </div>
          </div>

          {/* Dropdown Menu */}
          <Menu
            anchorEl={anchorElApp}
            open={open}
            onClose={handleCloseMenu}
            classes={{ paper: styles.dropdownMenu }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              style: {
                maxHeight: 300,
                width: anchorElApp ? anchorElApp.clientWidth : 'auto',
              },
            }}
          >
            {loading ? (
              <MenuItem disabled className={styles.dropdownMenuItem}>
                <CircularProgress size={24} />
              </MenuItem>
            ) : (
              getVersionList().map((appVersion: AppVersionObjectType) => (
                <MenuItem
                  key={appVersion.app_os_name || appVersion.app_version}
                  onClick={() => handleSelectappversion(appVersion)}
                  className={`${styles.dropdownMenuItem} ${
                    appversionOsName === appVersion.app_os_name ? styles.dropdownMenuItemSelected : ''
                  }`}
                >
                  <ListItemText 
                    primary={appVersion.app_os_name || appVersion.app_version} 
                    secondary={appVersion.app_version ? `v${appVersion.app_version}` : ''}
                  />
                </MenuItem>
              ))
            )}
          </Menu>
        </form>
      )}
    </>
  );
};

export default AppVersion;